import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `/api`;

// Configure axios defaults for better reliability
axios.defaults.timeout = 30000; // 30 second timeout

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            if (token) {
                // Retry /auth/me up to 3 times on server errors
                for (let attempt = 1; attempt <= 3; attempt++) {
                    try {
                        const response = await axios.get(`${API}/auth/me`, {
                            headers: { Authorization: `Bearer ${token}` },
                            timeout: 15000
                        });
                        setUser(response.data);
                        break;
                    } catch (error) {
                        const isServerError = error.response?.status >= 500 || !error.response;
                        if (isServerError && attempt < 3) {
                            await new Promise(r => setTimeout(r, 1500 * attempt));
                            continue;
                        }
                        console.error('Auth error:', error);
                        if (error.response?.status === 401) {
                            logout();
                        }
                        break;
                    }
                }
            }
            setLoading(false);
        };
        initAuth();
    }, [token]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password }, {
            timeout: 30000 // 30 second timeout for login
        });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${token}`
    });

    return (
        <AuthContext.Provider value={{ user, token, loading, login, logout, getAuthHeaders }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
